import { PreconsumerScrapMaterialWithEmissionReport } from '@/api/PreconsumerScrapMaterialModel'
import { UnitLabel } from '@/shared/config/config'
import { PrecursorMaterial } from '@/hooks/api/v2/precursorMaterials'
import useI18n from '@/shared/hooks/useI18n'
import useOrganizationDocuments from '@/shared/hooks/useOrganizationDocuments'
import { useReportFormatting } from '@/shared/hooks/useReportFormatting'
import {
  Table,
  TableRow,
  TableColumnHeader,
  TableCell,
  TableFooterCell,
  generatePeriodId
} from '@/common'
import { useCallback, useEffect, useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'

function AllocatedPreconsumerScrapTableRow({
  material,
  link
}: {
  material: PreconsumerScrapMaterialWithEmissionReport
  link: string
}) {
  const { generateDownloadUrl } = useOrganizationDocuments()
  const { formatReportPeriod } = useReportFormatting()
  const [url, setUrl] = useState<string>('')

  useEffect(() => {
    const fetchUrl = async () => {
      if (material.proofFilePath) {
        const url = await generateDownloadUrl(material.proofFilePath)
        if (url) {
          setUrl(url)
        }
      }
    }
    if (!url) {
      fetchUrl()
    }
  }, [generateDownloadUrl, material.proofFilePath, url])

  return (
    <TableRow key={material.id}>
      <TableCell>{material.emissionReport?.productionRoute?.name}</TableCell>
      <TableCell>
        <NavLink className="text-blue-500 hover:underline" to={link}>
          {formatReportPeriod(
            new Date(material.emissionReport?.startDate ?? ''),
            new Date(material.emissionReport?.endDate ?? '')
          )}
        </NavLink>
      </TableCell>
      <TableCell>{material.internalId ?? '-'}</TableCell>
      <TableCell>
        {material.proofFilePath ? (
          <a href={url} download={material.proofFilePath} className="text-blue-500 hover:underline">
            {material.proofFilePath}
          </a>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell className="text-right font-mono">
        {material.quantityInTonnes} {UnitLabel.t}
      </TableCell>
    </TableRow>
  )
}

export default function AllocatedPreconsumerScrapTable({
  materials
}: {
  materials: PreconsumerScrapMaterialWithEmissionReport[]
}) {
  const { t } = useI18n()
  const installationId = useParams<{ installationId: string }>().installationId!

  const getReportLink = useCallback(
    (material: PreconsumerScrapMaterialWithEmissionReport | PrecursorMaterial) => {
      if (!material?.emissionReport) {return ''}
      const periodId = generatePeriodId(
        new Date(material?.emissionReport?.startDate ?? ''),
        new Date(material?.emissionReport?.endDate ?? '')
      )
      return `/dashboard/${installationId}/production-routes/${material?.emissionReport?.productionRoute?.id}/reports/${periodId}/view/${material?.emissionReport?.id}`
    },
    [installationId]
  )

  return (
    <Table>
      <thead>
        <TableRow>
          <TableColumnHeader className="w-56">
            {t('materials:allocatedStockTable.headers.productionRoute')}
          </TableColumnHeader>
          <TableColumnHeader>{t('materials:allocatedStockTable.headers.report')}</TableColumnHeader>
          <TableColumnHeader>
            {t('materials:allocatedStockTable.headers.internalId')}
          </TableColumnHeader>
          <TableColumnHeader>
            {t('materials:allocatedStockTable.headers.evidence')}
          </TableColumnHeader>
          <TableColumnHeader className="text-right">
            {t('materials:allocatedStockTable.headers.quantity')}
          </TableColumnHeader>
        </TableRow>
      </thead>
      <tbody>
        {materials.map(material => (
          <AllocatedPreconsumerScrapTableRow
            key={material.id}
            material={material}
            link={getReportLink(material)}
          />
        ))}
      </tbody>
      <tfoot>
        <TableRow>
          <TableFooterCell colSpan={4} align="right">
            {t('common:total')}
          </TableFooterCell>
          <TableFooterCell align="right">
            {materials.reduce((acc, material) => acc + material.quantityInTonnes, 0)} {UnitLabel.t}
          </TableFooterCell>
        </TableRow>
      </tfoot>
    </Table>
  )
}
